import React, { useState } from 'react';
import moment from 'moment';
import { Bill, FiscalpopCFDI } from '../../../models/Factura';
import { Receptor } from '../../../models/Catalogos';
import AutoComplete from '../../Forms/Autocomplete';
import Button from '../../Forms/Button';
import Checkbox from '../../Forms/Checkbox';
import Input from '../../Forms/Input';
import Pagination from '../../Forms/Pagination';
import Select from '../../Forms/Select';
import { formatCurrencyStyled } from '../../visuals/currency.formatted';
import { RELATED_PAGE_LIMIT, TIPO_RELACION } from '../constants/creditNote.options';

interface CreditNoteRelatedProps {
    cfdi: Bill;
    confirmedRelated: boolean;
    billRelates: FiscalpopCFDI[];
    cfdiRelatedOptions: FiscalpopCFDI[];
    receptorOptions: Receptor[];
    relatedTotal: number;
    // Emitters
    onReceptorChange: (property: 'nombre' | 'rfc' | 'usoCFDI' | 'email') => (value: string) => void;
    onReceptorAutofill: (receptorAsString: string) => void;
    onRelationType: (tipoRel: string) => void;
    onRelatedBillSelect: (billSelected: FiscalpopCFDI) => (selected: boolean) => void;
    onRelatedBillChange: () => void;
}

function CreditNoteRelated({
    cfdi,
    confirmedRelated,
    billRelates,
    cfdiRelatedOptions,
    receptorOptions,
    relatedTotal,
    onReceptorChange,
    onReceptorAutofill,
    onRelationType,
    onRelatedBillSelect,
    onRelatedBillChange }: CreditNoteRelatedProps) {

    const [billRelFilter, setBillRelateFilter] = useState('');
    const [billRelFilerPage, setBillRelatePage] = useState(1);

    const receptorNameOptions = receptorOptions.map(r => ({ label: r.name, value: JSON.stringify(r), template: <div><p className="_big">{r.name}</p><p className="_small">{r.rfc}</p></div> }))
    const receptorRFCOptions = receptorOptions.map(r => ({ label: r.rfc, value: JSON.stringify(r), template: <div><p className="_big">{r.rfc}</p><p className="_small">{r.name}</p></div> }))

    // Once confirmed the list only displays what was selected
    const searchFilter = billRelFilter.toUpperCase();
    const filteredBillRelates = !confirmedRelated ? billRelates.filter((bR) => {
        if (!billRelFilter) {
            return true;
        }
        return (bR.uuid.includes(searchFilter) || bR.json['@'].Serie.toUpperCase().includes(searchFilter) || bR.json['@'].Folio.toUpperCase().includes(searchFilter))
    }) : cfdiRelatedOptions
    const pageValue = billRelFilerPage * (RELATED_PAGE_LIMIT) >= filteredBillRelates.length ?
        Math.ceil(filteredBillRelates.length / RELATED_PAGE_LIMIT) :
        billRelFilerPage;

    const paginatedFilterBills = filteredBillRelates.filter((_, i) => {
        return (i >= (pageValue - 1) * RELATED_PAGE_LIMIT) && (i < (RELATED_PAGE_LIMIT * pageValue))
    });

    const handleRelatedBillChange = () => {
        if (!confirmedRelated) {
            setBillRelateFilter('');
        }
        onRelatedBillChange();
    }

    return (
        <div>
            <h4>Factura relacionada</h4>
            <div className={`card table ${confirmedRelated ? 'confirmed' : ''}`} id="billRelated">
                <div className="cardBody">
                    <div className="row two md-one sm-gap-compressed">
                        <div className="row two xs-one sm-gap-compressed">
                            <AutoComplete disabled={confirmedRelated} onChange={onReceptorChange('nombre')} onSelect={onReceptorAutofill} forcePropsValue={true} noChangeOnSelect={true} label="Razón social del receptor" value={cfdi.receptor.nombre} placeholder="Razón social del receptor" type="text" options={receptorNameOptions} />
                            <AutoComplete disabled={confirmedRelated} onChange={onReceptorChange('rfc')} onSelect={onReceptorAutofill} forcePropsValue={true} noChangeOnSelect={true} label="Rfc receptor" value={cfdi.receptor.rfc} placeholder="RFC del receptor" type="text" options={receptorRFCOptions} />
                        </div>
                        <div className="row">
                            <Select disabled={confirmedRelated} label="Tipo de relación" value={cfdi.cfdiRelacionados.tipoRelacion} placeholder="" onChange={onRelationType} options={TIPO_RELACION} />
                        </div>
                    </div>
                    <h4>Factura a relacionar</h4>
                    <div className="card">
                        {
                            billRelates.length ?
                                <>
                                    <div className="relatedFilter">
                                        <Input type="text" disabled={confirmedRelated} value={billRelFilter} onChange={setBillRelateFilter} label="Filtrar facturas" placeholder="Busca por Serie, Folio o UUID" />
                                    </div>
                                    <div className="billOptions header">
                                        <div />
                                        <p className="date">
                                            Fecha de emisión
                                        </p>
                                        <p className="serie">
                                            Serie / Folio
                                        </p>
                                        <p className="uuid">
                                            Conceptos / UUID
                                        </p>
                                        <p>
                                            Monto
                                        </p>
                                    </div>
                                </>
                                : (

                                    <h3 className="instructions">
                                        Elige un receptor guardado o llena sus datos para relacionar una factura
                                    </h3>
                                )
                        }
                        {
                            paginatedFilterBills.map((bill) => {
                                const selectedBill = cfdiRelatedOptions.some(cRO => cRO.uuid === bill.uuid);
                                const conceptos = bill.json['cfdi:Conceptos']['cfdi:Concepto'];
                                return (
                                    <div className="billOptions" key={bill.uuid}>
                                        <Checkbox disabled={confirmedRelated} value={selectedBill} onChange={onRelatedBillSelect(bill)} />
                                        <p className="date">
                                            {moment(bill.json['@'].Fecha).format('DD / MMM / YYYY')}
                                        </p>
                                        <div className="serie">
                                            <p>
                                                Serie: {bill.json['@'].Serie}
                                            </p>
                                            <p className="small">
                                                Folio: {bill.json['@'].Folio}
                                            </p>
                                        </div>
                                        <div className="uuid">
                                            <p>
                                                {conceptos.length > 1 ? `${conceptos.length} conceptos` : conceptos[0]['@'].Descripcion}
                                            </p>
                                            <p className="small">
                                                {bill.uuid}
                                            </p>
                                        </div>
                                        <p>
                                            {formatCurrencyStyled(parseFloat(bill.json['@'].Total))}
                                        </p>
                                    </div>
                                )
                            })
                        }
                        <Pagination onPage={setBillRelatePage} page={pageValue} pages={Math.ceil(filteredBillRelates.length / RELATED_PAGE_LIMIT) || 1} />
                    </div>
                    <div className="relActions">
                        <div className="billPreview">
                            {
                                relatedTotal ?
                                    <p>Total de facturas relacionadas: {formatCurrencyStyled(relatedTotal)}</p>
                                    : ''
                            }
                        </div>
                        <Button primary={true} handleClick={handleRelatedBillChange} disabled={!cfdiRelatedOptions.length}>
                            {
                                confirmedRelated ?
                                    <span>
                                        Cambiar facturas relacionadas
                                    </span>
                                    :
                                    <span>
                                        Seleccionar facturas relacionadas
                                    </span>
                            }
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default CreditNoteRelated;
